import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format } from 'date-fns';
import api from '../api';

interface ChartData {
  account: string;
  [company: string]: number | string;
}

interface Session {
  id: number;
  name: string;
  created_at: string;
}

interface SessionData {
  session: Session;
  data: ChartData[];
}

const ReportChart: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showTopAccounts, setShowTopAccounts] = useState(20);
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionId) {
      fetchSessionData(parseInt(sessionId));
    }
  }, [sessionId]);

  const fetchSessionData = async (id: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/session/${id}`);
      setSessionData(response.data);
    } catch (error: any) {
      console.error('Error fetching session data:', error);
      setError(error.response?.data?.message || 'Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/reports');
  };

  const handleRefresh = () => {
    if (sessionId) {
      fetchSessionData(parseInt(sessionId));
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  // Get unique company names for chart colors
  const getCompanyNames = () => {
    if (!sessionData?.data || sessionData.data.length === 0) return [];
    
    const companies = new Set<string>();
    sessionData.data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'account' && key !== 'Total') {
          companies.add(key);
        }
      });
    });
    
    return Array.from(companies);
  };

  // Color palette for companies
  const colors = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1',
    '#d084d0', '#ffb347', '#87ceeb', '#dda0dd', '#98fb98'
  ];

  const companies = getCompanyNames();

  // Calculate summary statistics
  const calculateSummary = () => {
    if (!sessionData?.data) return { totalAccounts: 0, totalAmount: 0, companies: [] };
    
    const totalAccounts = sessionData.data.length;
    const totalAmount = sessionData.data.reduce((sum, item) => sum + (item.Total as number || 0), 0);
    
    return {
      totalAccounts,
      totalAmount,
      companies: companies.length
    };
  };

  const summary = calculateSummary();

  // Classify accounts consistently for table aggregation
  const classifyAccount = (accountName: string) => {
    let type = 'Other';
    const name = accountName.toLowerCase();
    if (name.includes('bank') || name.includes('cash') || name.includes('checking')) {
      type = 'Cash & Bank';
    } else if (name.includes('receivable') || name.includes('ar ') || name.includes('a/r')) {
      type = 'Receivables';
    } else if (name.includes('payable') || name.includes('ap ') || name.includes('a/p')) {
      type = 'Payables';
    } else if (name.includes('income') || name.includes('revenue') || name.includes('fees')) {
      type = 'Income';
    } else if (name.includes('expense') || name.includes('cost')) {
      type = 'Expenses';
    } else if (name.includes('asset')) {
      type = 'Assets';
    } else if (name.includes('liability')) {
      type = 'Liabilities';
    }
    return type;
  };

  // Aggregate totals by company
  const getCompanyTotals = () => {
    if (!sessionData?.data) return [] as Array<Record<string, number | string>>;

    const totals: Record<string, number> = {};
    companies.forEach(c => (totals[c] = 0));
    let grandTotal = 0;

    sessionData.data.forEach(item => {
      companies.forEach(c => {
        totals[c] += Math.abs((item[c] as number) || 0);
      });
      grandTotal += Math.abs((item.Total as number) || 0);
    });

    const row: Record<string, number | string> = { metric: 'Total Amount' };
    companies.forEach(c => (row[c] = totals[c]));
    row['Total'] = grandTotal;
    const accountsRow: Record<string, number | string> = { metric: 'Total Accounts' };
    companies.forEach(c => (accountsRow[c] = sessionData.data.length));
    accountsRow['Total'] = sessionData.data.length;

    return [row, accountsRow];
  };

  // Build matrix of account types by company for table view
  const getTypeCompanyMatrix = () => {
    if (!sessionData?.data) return [] as Array<Record<string, number | string>>;

    const accountTypes = [
      'Cash & Bank',
      'Receivables',
      'Payables',
      'Income',
      'Expenses',
      'Assets',
      'Liabilities',
      'Other',
    ];

    const matrix: Record<string, Record<string, number>> = {};
    accountTypes.forEach(t => (matrix[t] = {}));

    sessionData.data.forEach(item => {
      const type = classifyAccount(item.account);
      companies.forEach(c => {
        matrix[type][c] = (matrix[type][c] || 0) + Math.abs((item[c] as number) || 0);
      });
      matrix[type]['Total'] = (matrix[type]['Total'] || 0) + Math.abs((item.Total as number) || 0);
    });

    return accountTypes.map(type => {
      const row: Record<string, number | string> = { metric: type };
      companies.forEach(c => (row[c] = matrix[type][c] || 0));
      row['Total'] = matrix[type]['Total'] || 0;
      return row;
    });
  };

  // Prepare pie chart data for company totals
  const getPieChartData = () => {
    if (!sessionData?.data) return [];
    
    const companyTotals = companies.map((company, index) => {
      const total = sessionData.data.reduce((sum, item) => {
        return sum + (item[company] as number || 0);
      }, 0);
      
      return {
        name: company,
        value: Math.abs(total),
        color: colors[index % colors.length]
      };
    });
    
    return companyTotals.filter(item => item.value > 0);
  };

  // Get top accounts for detailed view
  const getTopAccounts = () => {
    if (!sessionData?.data) return [];
    
    const accountCount = showAllAccounts ? sessionData.data.length : showTopAccounts;
    return sessionData.data.slice(0, accountCount);
  };

  // Get account type breakdown
  const getAccountTypeBreakdown = () => {
    if (!sessionData?.data) return [];
    
    const types = sessionData.data.reduce((acc, item) => {
      // Categorize accounts by type based on name
      let type = 'Other';
      const accountName = item.account.toLowerCase();
      
      if (accountName.includes('bank') || accountName.includes('cash') || accountName.includes('checking')) {
        type = 'Cash & Bank';
      } else if (accountName.includes('receivable') || accountName.includes('ar ') || accountName.includes('a/r')) {
        type = 'Receivables';
      } else if (accountName.includes('payable') || accountName.includes('ap ') || accountName.includes('a/p')) {
        type = 'Payables';
      } else if (accountName.includes('income') || accountName.includes('revenue') || accountName.includes('fees')) {
        type = 'Income';
      } else if (accountName.includes('expense') || accountName.includes('cost')) {
        type = 'Expenses';
      } else if (accountName.includes('asset')) {
        type = 'Assets';
      } else if (accountName.includes('liability')) {
        type = 'Liabilities';
      }
      
      if (!acc[type]) acc[type] = 0;
      acc[type] += Math.abs(item.Total as number || 0);
      
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(types).map(([name, value], index) => ({
      name,
      value,
      color: colors[index % colors.length]
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Reports
        </Button>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!sessionData) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Reports
        </Button>
        <Alert severity="info">
          Session not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 1 }}
          >
            Back to Reports
          </Button>
          <Typography variant="h4">
            {sessionData.session.name || `Report ${sessionData.session.id}`}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Created: {formatDate(sessionData.session.created_at)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={() => setActiveTab(4)}
          >
            Table View
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => {
              // TODO: Implement export functionality
              console.log('Export functionality to be implemented');
            }}
          >
            Export
          </Button>
        </Stack>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Total Accounts
              </Typography>
              <Typography variant="h4">
                {summary.totalAccounts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h4">
                {formatCurrency(summary.totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Companies
              </Typography>
              <Typography variant="h4">
                {summary.companies}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      {sessionData.data.length > 0 ? (
        <Box>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ mb: 3 }}
          >
            <Tab label="Overview" />
            <Tab label="Company Breakdown" />
            <Tab label="Account Details" />
            <Tab label="Account Types" />
            <Tab label="Table View" />
          </Tabs>

          {/* Tab 0: Overview */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Distribution by Company
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={getPieChartData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {getPieChartData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Account Type Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={getAccountTypeBreakdown()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {getAccountTypeBreakdown().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: Company Breakdown */}
          {activeTab === 1 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Financial Summary by Company
              </Typography>
              <ResponsiveContainer width="100%" height={500}>
                <BarChart
                  data={getPieChartData()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis tickFormatter={formatCurrency} />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#8884d8">
                    {getPieChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Tab 2: Account Details */}
          {activeTab === 2 && (
            <Paper sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Account Details
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <FormControl size="small">
                    <InputLabel>Show Accounts</InputLabel>
                    <Select
                      value={showTopAccounts}
                      label="Show Accounts"
                      onChange={(e) => setShowTopAccounts(e.target.value as number)}
                      disabled={showAllAccounts}
                    >
                      <MenuItem value={10}>Top 10</MenuItem>
                      <MenuItem value={20}>Top 20</MenuItem>
                      <MenuItem value={50}>Top 50</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showAllAccounts}
                        onChange={(e) => setShowAllAccounts(e.target.checked)}
                      />
                    }
                    label="Show All"
                  />
                </Box>
              </Box>
              
              {/* Company Legend */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Companies
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {companies.map((company, index) => (
                    <Chip
                      key={company}
                      label={company}
                      sx={{
                        backgroundColor: colors[index % colors.length],
                        color: 'white',
                        mb: 1
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <ResponsiveContainer width="100%" height={700}>
                <BarChart
                  data={getTopAccounts()}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 180,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="account" 
                    angle={-45}
                    textAnchor="end"
                    height={150}
                    interval={0}
                  />
                  <YAxis 
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip 
                    formatter={(value: number) => formatCurrency(value)}
                    labelStyle={{ color: '#000' }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '100px' }}
                    verticalAlign="bottom"
                    height={36}
                  />
                  {companies.map((company, index) => (
                    <Bar
                      key={company}
                      dataKey={company}
                      fill={colors[index % colors.length]}
                      name={company}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {getTopAccounts().length} accounts out of {sessionData.data.length} total
              </Typography>
            </Paper>
          )}

          {/* Tab 3: Account Types */}
          {activeTab === 3 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Account Types Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={500}>
                <BarChart
                  data={getAccountTypeBreakdown()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis tickFormatter={formatCurrency} />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#8884d8">
                    {getAccountTypeBreakdown().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Tab 4: Table View */}
          {activeTab === 4 && (
            <Box>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  High-Level Snapshot
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Metric</TableCell>
                        {companies.map(c => (
                          <TableCell key={c} align="right">{c}</TableCell>
                        ))}
                        <TableCell align="right">Total / Consolidated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getCompanyTotals().map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.metric as string}</TableCell>
                          {companies.map(c => (
                            <TableCell key={c} align="right">{formatCurrency((row[c] as number) || 0)}</TableCell>
                          ))}
                          <TableCell align="right">{formatCurrency((row['Total'] as number) || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Account Type Summary
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Metric</TableCell>
                        {companies.map(c => (
                          <TableCell key={c} align="right">{c}</TableCell>
                        ))}
                        <TableCell align="right">Total / Consolidated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getTypeCompanyMatrix().map((row) => (
                        <TableRow key={row.metric as string}>
                          <TableCell>{row.metric as string}</TableCell>
                          {companies.map(c => (
                            <TableCell key={c} align="right">{formatCurrency((row[c] as number) || 0)}</TableCell>
                          ))}
                          <TableCell align="right">{formatCurrency((row['Total'] as number) || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Data Available
          </Typography>
          <Typography variant="body1" color="textSecondary">
            No financial data was found for this session.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ReportChart; 